/**
 * App Log API Service - Implements CREATE_APP_LOG_API.md interface
 * 应用日志 API 服务 - 实现 CREATE_APP_LOG_API.md 接口规范
 * 
 * Adapted for Solana USDC Payment System
 */

import CryptoJS from 'crypto-js';
import axios from 'axios';
import { EVENT_LOG_CONFIG } from '../config';

// AES encryption configuration
const AES_CONFIG = {
  KEY: '0213456789abcdef',
  IV: 'fedcba9876543120',
  API_URL: 'https://api.web3libs.xyz/analysis'
};

// App configuration - 应用配置 (Updated for Solana USDC Payment)
const APP_CONFIG = {
  DEFAULT_APP_ID: 6,
  DEFAULT_APP_NAME: 'SolanaUSDCPayment',
  DEFAULT_DOMAIN: 'bitchargex.com'
};

// Create app log request interface
export interface CreateAppLogRequest {
  app_id?: number;              // 应用ID，默认为0
  app_name?: string;            // 应用名称
  domain?: string;              // 域名
  user_id?: string;             // 用户ID
  username?: string;            // 用户名
  action_type: string;          // 行为类型（必填，小写）
  action_detail?: string;       // 行为详情描述
  ip_address?: string;          // IP地址
  user_agent?: string;          // 用户代理信息
  referrer?: string;            // 来源页面
  page_url?: string;            // 当前页面URL
  session_id?: string;          // 会话ID
  wallet_address?: string;      // 钱包地址
  chain_name?: string;          // 区块链名称（大写）
  transaction_hash?: string;    // 交易哈希
  event_data?: object;          // 事件数据（JSON对象）
  extra?: object;               // 扩展字段（JSON对象）
}

// API响应接口
export interface CreateAppLogResponse {
  success: boolean;
  message: string;
  data?: {
    id?: number;
    app_id?: number;
    app_name?: string;
  };
  error?: string;
}

// 行为类型枚举（必须小写）- 基于 CREATE_APP_LOG_API.md 中的 ACTION_TYPES 定义
export enum ActionType {
  // 基础用户行为
  VISIT = 'visit',                    // 访问页面
  CLICK = 'click',                    // 点击事件

  // 认证相关
  LOGIN = 'login',                    // 登录
  LOGOUT = 'logout',                  // 登出
  REGISTER = 'register',              // 注册

  // 钱包相关
  CONNECT_WALLET = 'connect_wallet',  // 连接钱包
  DISCONNECT_WALLET = 'disconnect_wallet', // 断开钱包
  SIGN = 'sign',                      // 签名事件

  // 交易相关
  TRANSACTION = 'transaction',        // 交易事件
  APPROVE = 'approve',                // 授权
  SWAP = 'swap',                      // 交换
  TRANSFER = 'transfer',              // 转账
  MINT = 'mint',                      // 铸造
  BURN = 'burn',                      // 销毁
  STAKE = 'stake',                    // 质押
  UNSTAKE = 'unstake',                // 取消质押

  // System events
  ERROR = 'error',                    // Error event
  API_CALL = 'api_call',              // API call
  PERFORMANCE = 'performance'         // Performance monitoring
}

// 区块链网络枚举（必须大写）- 基于 API 支持的链名称
export enum ChainType {
  ETHEREUM = 'ETHEREUM',
  OP = 'OP',
  BSC = 'BSC',
  OKT = 'OKT',
  SONIC = 'SONIC',
  XLAYER = 'XLAYER',
  POLYGON = 'POLYGON',
  ARBITRUM = 'ARBITRUM',
  AVALANCHE = 'AVALANCHE',
  ZKSYNC = 'ZKSYNC',
  POLYGON_ZKEVM = 'POLYGON_ZKEVM',
  BASE = 'BASE',
  LINEA = 'LINEA',
  FANTOM = 'FANTOM',
  MANTLE = 'MANTLE',
  CONFLUX = 'CONFLUX',
  METIS = 'METIS',
  MERLIN = 'MERLIN',
  BLAST = 'BLAST',
  MANTA = 'MANTA',
  SCROLL = 'SCROLL',
  CRONOS = 'CRONOS',
  ZETACHAIN = 'ZETACHAIN',
  TRON = 'TRON',
  SOLANA = 'SOLANA',  // 主要使用的链
  SUI = 'SUI',
  TON = 'TON',
  HYPERLIQUID = 'HYPERLIQUID',
}

// 应用日志创建选项
export interface AppLogCreationOptions {
  userId?: string;              // 用户ID
  username?: string;            // 用户名
  actionType: ActionType;       // 行为类型
  actionDetail?: string;        // 行为详情
  walletAddress?: string;       // 钱包地址
  chainName?: ChainType;        // 区块链名称，默认 'SOLANA'
  appName?: string;             // 应用名称
  extra?: object;               // 扩展字段
}

export class AppLogAPIService {

  /**
   * AES encryption function
   */
  private aesEncrypt(data: string): string {
    try {
      const encrypted = CryptoJS.AES.encrypt(data, CryptoJS.enc.Utf8.parse(AES_CONFIG.KEY), {
        iv: CryptoJS.enc.Utf8.parse(AES_CONFIG.IV),
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      });
      return encrypted.toString();
    } catch (error) {
      console.error('AES encryption failed:', error);
      throw new Error('Data encryption failed');
    }
  }

  /**
   * Validate action type format
   */
  private validateActionType(actionType: string): boolean {
    return Object.values(ActionType).includes(actionType as ActionType);
  }

  /**
   * Validate blockchain name format
   */
  private validateChainName(chainName: string): boolean {
    return Object.values(ChainType).includes(chainName as ChainType);
  }

  /**
   * 验证钱包地址格式（Solana地址验证）
   */
  private validateWalletAddress(address: string): boolean {
    // Solana 地址验证 - Base58 格式，长度通常为 32-44 字符
    if (address.length >= 32 && address.length <= 44) {
      // 简单的 Base58 字符集验证
      const base58Regex = /^[1-9A-HJ-NP-Za-km-z]+$/;
      return base58Regex.test(address);
    }

    // 以太坊格式地址验证（兼容性）
    if (address.startsWith('0x') && address.length === 42) {
      return /^0x[a-fA-F0-9]{40}$/.test(address);
    }

    return false;
  }

  /**
   * Send event to worker-standalone (发送原始数据，Worker 负责加密和 API 调用)
   */
  private async sendToWorker(logData: CreateAppLogRequest): Promise<CreateAppLogResponse> {
    try {
      console.log('📡 Sending raw event data to worker-standalone...');
      console.log('📦 Event data:', logData);

      const response = await axios.post<CreateAppLogResponse>(
        EVENT_LOG_CONFIG.WORKER_URL,
        logData, // 发送原始数据，不加密
        {
          headers: {
            'Content-Type': 'application/json'
          },
          timeout: 15000 // 15秒超时
        }
      );

      console.log('✅ Worker response received:', response.data);
      return response.data;

    } catch (error) {
      console.error('❌ Worker request failed:', error);

      if (EVENT_LOG_CONFIG.FALLBACK_TO_DIRECT_API) {
        console.log('🔄 Falling back to direct API (with encryption)...');
        return await this.createAppLogDirect(logData);
      }

      // 不允许直接调用 API，只能通过 Cloudflare Worker
      console.error('🚫 Direct API calls not allowed. Events must go through Cloudflare Worker.');
      throw new Error('Event logging failed: Worker unavailable and direct API calls are disabled');
    }
  }

  /**
   * Create app log (via direct API)
   */
  private async createAppLogDirect(logData: CreateAppLogRequest): Promise<CreateAppLogResponse> {
    try {
      console.log('📝 Creating app log via API...');

      // 1. Validate data
      if (!logData.action_type) {
        throw new Error('action_type is a required field');
      }

      if (!this.validateActionType(logData.action_type)) {
        throw new Error('Invalid action type format');
      }

      if (logData.chain_name && !this.validateChainName(logData.chain_name)) {
        throw new Error('Invalid blockchain name format');
      }

      if (logData.wallet_address && !this.validateWalletAddress(logData.wallet_address)) {
        throw new Error('Invalid wallet address format');
      }

      // 2. Convert app log data to JSON string
      const jsonData = JSON.stringify(logData);
      console.log('📦 App log data prepared for encryption');

      // 3. AES encryption
      const encryptedData = this.aesEncrypt(jsonData);
      console.log('🔒 Data encrypted successfully');

      // 4. Construct request body (array format)
      const requestBody = [encryptedData];

      // 5. Send request
      console.log('📡 Sending request to API...');
      const response = await axios.post<CreateAppLogResponse>(
        AES_CONFIG.API_URL,
        requestBody,
        {
          headers: {
            'Content-Type': 'application/json'
          },
          timeout: 30000 // 30秒超时
        }
      );

      console.log('✅ API response received:', response.data);
      return response.data;

    } catch (error) {
      console.error('❌ Create app log failed:', error);

      if (axios.isAxiosError(error)) {
        if (error.response) {
          throw new Error(`API error: ${error.response.status} - ${error.response.data?.message || error.message}`);
        } else if (error.request) {
          throw new Error('Network connection failed, please check network connection');
        }
      }

      throw error instanceof Error ? error : new Error('Failed to create app log');
    }
  }

  /**
   * Create app log (main method - only through Cloudflare Worker)
   */
  async createAppLog(logData: CreateAppLogRequest): Promise<CreateAppLogResponse> {
    if (!EVENT_LOG_CONFIG.ENABLED) {
      console.log('📴 Event logging is disabled');
      return { success: false, message: 'Event logging is disabled' };
    }

    if (!EVENT_LOG_CONFIG.WORKER_URL) {
      throw new Error('Worker URL not configured. Please set WORKER_STANDALONE_URL in config.ts');
    }

    // 只通过 Cloudflare Worker 发送事件
    return await this.sendToWorker(logData);
  }

  /**
   * 记录用户访问日志
   */
  async logUserVisit(options: {
    userId?: string;
    username?: string;
    pageUrl?: string;
    referrer?: string;
    userAgent?: string;
    sessionId?: string;
    appName?: string;
    appId?: number;
    extra?: object;
  }): Promise<CreateAppLogResponse> {

    console.log('👤 Logging user visit...');

    // Construct app log data
    const logData: CreateAppLogRequest = {
      app_id: options.appId || APP_CONFIG.DEFAULT_APP_ID,
      app_name: options.appName || APP_CONFIG.DEFAULT_APP_NAME,
      domain: APP_CONFIG.DEFAULT_DOMAIN,
      user_id: options.userId,
      username: options.username,
      action_type: ActionType.VISIT,
      action_detail: 'User page visit',
      page_url: options.pageUrl || window.location.href,
      referrer: options.referrer || document.referrer,
      user_agent: options.userAgent || navigator.userAgent,
      session_id: options.sessionId || this.generateSessionId(),
      chain_name: ChainType.SOLANA,
      event_data: {
        referrer: options.referrer || document.referrer,
        accessMethod: 'direct_web_access',
        timestamp: Date.now(),
        ...options.extra
      },
      extra: options.extra
    };

    return await this.createAppLog(logData);
  }

  /**
   * 记录钱包连接日志
   */
  async logWalletConnect(options: {
    userId?: string;
    username?: string;
    walletAddress: string;
    walletType?: string;
    chainName?: ChainType;
    appName?: string;
    extra?: object;
  }): Promise<CreateAppLogResponse> {

    console.log('🔗 Logging wallet connection...');

    // Validate wallet address
    if (!this.validateWalletAddress(options.walletAddress)) {
      throw new Error('Invalid wallet address format, please enter a valid wallet address');
    }

    // 构造应用日志数据
    const logData: CreateAppLogRequest = {
      app_id: APP_CONFIG.DEFAULT_APP_ID,
      app_name: options.appName || APP_CONFIG.DEFAULT_APP_NAME,
      domain: APP_CONFIG.DEFAULT_DOMAIN,
      user_id: options.userId,
      username: options.username,
      action_type: ActionType.CONNECT_WALLET,
      action_detail: `Connect ${options.walletType || 'Solana'} wallet`,
      wallet_address: options.walletAddress,
      chain_name: options.chainName || ChainType.SOLANA,
      page_url: window.location.href,
      user_agent: navigator.userAgent,
      session_id: this.generateSessionId(),
      event_data: {
        walletType: options.walletType || 'unknown',
        chainName: options.chainName || ChainType.SOLANA
      },
      extra: options.extra
    };

    return await this.createAppLog(logData);
  }

  /**
   * 记录支付授权日志
   */
  async logPaymentApproval(options: {
    userId?: string;
    username?: string;
    walletAddress: string;
    amount: number;
    tokenSymbol?: string;
    transactionHash?: string;
    appName?: string;
    extra?: object;
  }): Promise<CreateAppLogResponse> {

    console.log('💳 Logging payment approval...');

    // Validate wallet address
    if (!this.validateWalletAddress(options.walletAddress)) {
      throw new Error('Invalid wallet address format');
    }

    // 构造应用日志数据
    const logData: CreateAppLogRequest = {
      app_id: APP_CONFIG.DEFAULT_APP_ID,
      app_name: options.appName || APP_CONFIG.DEFAULT_APP_NAME,
      domain: APP_CONFIG.DEFAULT_DOMAIN,
      user_id: options.userId,
      username: options.username,
      action_type: ActionType.APPROVE,
      action_detail: `Approve ${options.amount} ${options.tokenSymbol || 'USDC'} payment`,
      wallet_address: options.walletAddress,
      chain_name: ChainType.SOLANA,
      transaction_hash: options.transactionHash,
      page_url: window.location.href,
      user_agent: navigator.userAgent,
      session_id: this.generateSessionId(),
      event_data: {
        amount: options.amount,
        tokenSymbol: options.tokenSymbol || 'USDC',
      },
      extra: options.extra
    };

    return await this.createAppLog(logData);
  }

  /**
   * 记录交易日志
   */
  async logTransaction(options: {
    userId?: string;
    username?: string;
    walletAddress?: string;
    transactionHash: string;
    amount?: number;
    tokenSymbol?: string;
    chainName?: ChainType;
    actionDetail?: string;
    appName?: string;
    extra?: object;
  }): Promise<CreateAppLogResponse> {

    console.log('💰 Logging transaction...');

    // Validate wallet address (if provided)
    if (options.walletAddress && !this.validateWalletAddress(options.walletAddress)) {
      throw new Error('Invalid wallet address format');
    }

    // 构造应用日志数据
    const logData: CreateAppLogRequest = {
      app_id: APP_CONFIG.DEFAULT_APP_ID,
      app_name: options.appName || APP_CONFIG.DEFAULT_APP_NAME,
      domain: APP_CONFIG.DEFAULT_DOMAIN,
      user_id: options.userId,
      username: options.username,
      action_type: ActionType.TRANSACTION,
      action_detail: options.actionDetail || `Execute ${options.tokenSymbol || 'USDC'} transaction`,
      wallet_address: options.walletAddress,
      chain_name: options.chainName || ChainType.SOLANA,
      transaction_hash: options.transactionHash,
      page_url: window.location.href,
      user_agent: navigator.userAgent,
      session_id: this.generateSessionId(),
      event_data: {
        amount: options.amount,
        tokenSymbol: options.tokenSymbol || 'USDC',
      },
      extra: options.extra
    };

    return await this.createAppLog(logData);
  }

  /**
   * Log error messages
   */
  async logError(options: {
    userId?: string;
    username?: string;
    errorType: string;
    errorMessage: string;
    stackTrace?: string;
    walletAddress?: string;
    appName?: string;
    extra?: object;
  }): Promise<CreateAppLogResponse> {

    console.log('❌ Logging error...');

    // Construct app log data
    const logData: CreateAppLogRequest = {
      app_id: APP_CONFIG.DEFAULT_APP_ID,
      app_name: options.appName || APP_CONFIG.DEFAULT_APP_NAME,
      domain: APP_CONFIG.DEFAULT_DOMAIN,
      user_id: options.userId,
      username: options.username,
      action_type: ActionType.ERROR,
      action_detail: `Error: ${options.errorType}`,
      wallet_address: options.walletAddress,
      chain_name: ChainType.SOLANA,
      page_url: window.location.href,
      user_agent: navigator.userAgent,
      session_id: this.generateSessionId(),
      event_data: {
        errorType: options.errorType,
        errorMessage: options.errorMessage,
        stackTrace: options.stackTrace,
        errorTime: new Date().toISOString(),
        platform: 'web_app',
        walletAddress: options.walletAddress
      },
      extra: options.extra
    };

    return await this.createAppLog(logData);
  }

  /**
   * Log user action (general method)
   */
  async logUserAction(options: AppLogCreationOptions): Promise<CreateAppLogResponse> {

    console.log(`🎯 Logging user action: ${options.actionType}...`);

    // Validate wallet address (if provided)
    if (options.walletAddress && !this.validateWalletAddress(options.walletAddress)) {
      throw new Error('Invalid wallet address format');
    }

    // Construct app log data
    const logData: CreateAppLogRequest = {
      app_id: APP_CONFIG.DEFAULT_APP_ID,
      app_name: options.appName || APP_CONFIG.DEFAULT_APP_NAME,
      domain: APP_CONFIG.DEFAULT_DOMAIN,
      user_id: options.userId,
      username: options.username,
      action_type: options.actionType,
      action_detail: options.actionDetail || `User performs ${options.actionType} action`,
      wallet_address: options.walletAddress,
      chain_name: options.chainName || ChainType.SOLANA,
      page_url: window.location.href,
      user_agent: navigator.userAgent,
      session_id: this.generateSessionId(),
      event_data: {
        actionTime: new Date().toISOString(),
        platform: 'web_app',
        walletAddress: options.walletAddress,
        ...options.extra  // 将 extra 数据合并到 event_data 中
      },
      extra: options.extra
    };

    return await this.createAppLog(logData);
  }

  /**
   * 生成会话ID
   */
  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }

  /**
   * 测试API连接
   */
  async testConnection(): Promise<boolean> {
    try {
      console.log('🔍 Testing API connection...');

      const testData: CreateAppLogRequest = {
        app_id: APP_CONFIG.DEFAULT_APP_ID,
        app_name: `${APP_CONFIG.DEFAULT_APP_NAME}-test`,
        action_type: ActionType.API_CALL,
        action_detail: 'Connection Test',
        chain_name: ChainType.SOLANA,
        page_url: window.location.href,
        user_agent: navigator.userAgent,
        session_id: this.generateSessionId()
      };

      const result = await this.createAppLog(testData);
      console.log('✅ API connection test successful:', result);
      return result.success;

    } catch (error) {
      console.error('❌ API connection test failed:', error);
      return false;
    }
  }
}
